import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  doc, collection, onSnapshot, setDoc, addDoc, deleteDoc,
  increment, getDoc, runTransaction, query, where, orderBy, limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { playSkitter } from '../utils/soundManager';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const today = () => new Date().toISOString().slice(0, 10);

const msToMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight - now;
};

const fmtCountdown = ms => {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const getISOWeek = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    d.getFullYear() +
    '-W' +
    String(1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)).padStart(2, '0')
  );
};

const PERIOD_DOC = period => {
  const t = today();
  if (period === 'today') return `day-${t}`;
  if (period === 'week') return `week-${getISOWeek()}`;
  if (period === 'month') return `month-${t.slice(0, 7)}`;
  return 'alltime';
};

const PERIOD_LABELS = { today: 'Today', week: 'This Week', month: 'This Month', alltime: 'All Time' };

const TIERS = [
  {
    label: 'Glazing',
    color: '#cc44ff',
    borderColor: 'rgba(204,68,255,0.3)',
    options: [
      { key: 'sick',       label: 'On A Sick One',              emoji: '😛' },
      { key: 'assignment', label: 'Understood The Assignment',   emoji: '💅' },
      { key: 'smashing',   label: 'Smashing',                    emoji: '😎' },
      { key: 'eating',     label: 'Actually Eating',             emoji: '🔥' },
      { key: 'stan',       label: 'I Lowkey Stan',               emoji: '💜' },
    ],
  },
  {
    label: 'Neutral / Funny',
    color: 'rgba(255,255,255,0.55)',
    borderColor: 'rgba(255,255,255,0.12)',
    options: [
      { key: 'vibing', label: 'Just Vibing Lowkey', emoji: '🌙' },
      { key: 'blunt',  label: 'Needs A Blunt',      emoji: '💨' },
      { key: 'mid',    label: 'Straight Mid',        emoji: '😐' },
    ],
  },
  {
    label: 'Cooked',
    color: '#f0a040',
    borderColor: 'rgba(240,160,64,0.3)',
    options: [
      { key: 'win',     label: 'Bro Needs A Win', emoji: '😔' },
      { key: 'downbad', label: 'Down Bad',         emoji: '📉' },
      { key: 'cooked',  label: 'Cooked',           emoji: '🫠' },
    ],
  },
  {
    label: 'Hating / Violating',
    color: '#ff5555',
    borderColor: 'rgba(255,85,85,0.3)',
    options: [
      { key: 'clown',  label: 'Certified Clown',      emoji: '🤡' },
      { key: 'finest', label: "Not His Finest Era",    emoji: '💀' },
      { key: 'lame',   label: "Foo's A Lame",          emoji: '🥱' },
      { key: 'fts',    label: 'Fuck That Foo',         emoji: '🖕' },
      { key: 'trash',  label: 'Trash and No Bitches',  emoji: '🚮' },
      { key: 'weirdo', label: 'Fuckin Weirdo',         emoji: '🤨' },
    ],
  },
];

const ALL_OPTIONS = TIERS.flatMap(t => t.options);

const defaultVotes = () => Object.fromEntries(ALL_OPTIONS.map(o => [o.key, 0]));

const getTierForKey = key => TIERS.find(t => t.options.some(o => o.key === key));

// ── helpers that reference TIERS / ALL_OPTIONS (placed after those consts) ────

const getPrevISOWeek = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    d.getFullYear() +
    '-W' +
    String(1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)).padStart(2, '0')
  );
};

const TIER_SCORES = { 'Glazing': 4, 'Neutral / Funny': 2.5, 'Cooked': 2, 'Hating / Violating': 1 };

const computeScore = voteData => {
  let total = 0, weighted = 0;
  TIERS.forEach(tier => {
    const s = TIER_SCORES[tier.label] ?? 2;
    tier.options.forEach(opt => {
      const n = voteData[`vote_${opt.key}`] ?? 0;
      total += n;
      weighted += n * s;
    });
  });
  return total > 0 ? weighted / total : 0;
};

const getStreak = () => {
  const last = localStorage.getItem('vibeCheckLastDate');
  const streak = parseInt(localStorage.getItem('vibeCheckStreak') || '0', 10);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yd = yesterday.toISOString().slice(0, 10);
  if (last === today() || last === yd) return streak;
  return 0;
};

const updateStreak = () => {
  const last = localStorage.getItem('vibeCheckLastDate');
  const streak = parseInt(localStorage.getItem('vibeCheckStreak') || '0', 10);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yd = yesterday.toISOString().slice(0, 10);
  const newStreak = (last === yd || last === today()) ? streak + 1 : 1;
  localStorage.setItem('vibeCheckStreak', String(newStreak));
  localStorage.setItem('vibeCheckLastDate', today());
  return newStreak;
};

// ─────────────────────────────────────────────────────────────────────────────

const getWeekNumber = () => {
  const d = new Date();
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7);
};

const FLAVOR_POOLS = {
  sick: [
    "bro is IN IT right now. do not interrupt him.",
    "the vibes are immaculate. something is definitely about to go wrong.",
    "this era is giving main character. we're just NPCs in his arc.",
    "genuinely locked in. scientists are studying this.",
  ],
  assignment: [
    "he showed up and delivered. the bar was underground but still.",
    "surprisingly coherent this week. log it for the historians.",
    "okay fine. fine. we respect it.",
    "understood. delivered. went home. iconic.",
  ],
  smashing: [
    "genuinely doing pretty good. suspicious.",
    "we're not used to this. the algorithm is confused.",
    "solid week. not iconic but solid.",
    "he's smashing it. the people have spoken.",
  ],
  eating: [
    "thriving. absolutely thriving. scientists are baffled.",
    "secured the bag. ate. left no crumbs. ate the bag too.",
    "this man is EATING eating. we're proud.",
    "era of abundance. long may it continue.",
  ],
  stan: [
    "lowkey but we see you. we see the effort. 💜",
    "the soft appreciation is real. don't tell him.",
    "heart eyes but make it subtle.",
    "we're rooting for him and we're not even sorry about it.",
  ],
  vibing: [
    "not great not terrible. existing peacefully.",
    "background character arc. rest up king.",
    "quiet era. nobody bother him.",
    "he's fine. the energy is fine.",
  ],
  blunt: [
    "the stress is visible from orbit.",
    "he's fine he's fine he's fine he's definitely not fine",
    "someone get this man a snack and a couch",
    "the tension is palpable. someone intervene.",
  ],
  win: [
    "the universe is clowning him specifically this week",
    "it's giving rough patch. we're rooting for you though king",
    "chin up. the glow-up is loading.",
    "bro needs ONE win. just one. please.",
  ],
  downbad: [
    "certified disaster arc. the recovery era is incoming though",
    "rock bottom speedrun. he's so cooked it's almost art",
    "the metrics are not in his favor. chart is bad.",
    "we watched the descent in real time. thoughts and prayers.",
  ],
  clown: [
    "he walked himself into this one. we watched it happen in real time.",
    "the honkers are honking",
    "there's no malice here. just honks.",
    "certified clown behavior. no notes.",
  ],
  finest: [
    "the historians will study this week with concern.",
    "he knows. he knows.",
    "even the cemetery piano sounds sad right now.",
    "we're documenting this for the archives. 💀",
  ],
  lame: [
    "we said what we said.",
    "the foo is being a foo. it is what it is.",
    "we're not mad. just disappointed.",
    "lame behavior detected. council has been notified.",
  ],
  fts: [
    "the people have spoken and they are not pleased.",
    "this is a violation. a certified violation.",
    "unanimous decision. no appeals.",
    "the disrespect is immaculate actually.",
  ],
};

const getPostVoteComment = winnerKey => {
  const pool = FLAVOR_POOLS[winnerKey];
  if (!pool) return "the vibe has been checked. results are pending review. 🧾";
  return pool[getWeekNumber() % pool.length];
};

const soulsPhrase = count => {
  if (count <= 0) return null;
  if (count === 1) return '👻 1 soul lurking in the shadows';
  if (count <= 3) return `🕯️ ${count} presences detected`;
  if (count <= 7) return `👁️ ${count} entities watching from the dark`;
  if (count <= 15) return `💀 ${count} souls haunting the cemetery`;
  if (count <= 30) return `🦇 ${count} restless spirits in attendance`;
  return `☠️ the cemetery overflows — ${count} souls in the dark`;
};

const VibeCheck = () => {
  const [votes, setVotes] = useState(defaultVotes());
  const [myVote, setMyVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [winnerKey, setWinnerKey] = useState(null);
  const [activePeriod, setActivePeriod] = useState('today');
  const [periodVotes, setPeriodVotes] = useState(defaultVotes());
  const [periodLoading, setPeriodLoading] = useState(false);
  const [countdown, setCountdown] = useState('');
  const resultsRef = useRef(null);

  // New state
  const [streak, setStreak] = useState(() => getStreak());
  const [soulsLurking, setSoulsLurking] = useState(0);
  const [takes, setTakes] = useState([]);
  const [takeInput, setTakeInput] = useState('');
  const [takeSubmitted, setTakeSubmitted] = useState(false);
  const sessionIdRef = useRef(`soul_${Math.random().toString(36).slice(2, 10)}`);
  const [todayCount, setTodayCount] = useState(0);
  const [caption, setCaption] = useState(null);
  const [trendDir, setTrendDir] = useState(null); // 'up' | 'down' | 'same' | null
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'vibeCheck', 'current');

    const unsub = onSnapshot(ref, async snap => {
      const now = Date.now();

      if (!snap.exists() || now - (snap.data().weekStart ?? 0) > WEEK_MS) {
        // Use a transaction so multiple open tabs don't all reset at once
        const fresh = { votes: defaultVotes(), weekStart: now };
        await runTransaction(db, async txn => {
          const current = await txn.get(ref);
          if (!current.exists() || now - (current.data().weekStart ?? 0) > WEEK_MS) {
            txn.set(ref, fresh);
          }
        }).catch(() => {});
        setVotes(fresh.votes);

        // Only clear vote if user hasn't voted today
        if (localStorage.getItem('vibeCheckDate') !== today()) {
          setMyVote(null);
          setWinnerKey(null);
        }
      } else {
        const data = snap.data();
        const v = { ...defaultVotes(), ...(data.votes ?? {}) };
        setVotes(v);
        // Daily gate: check if user voted today
        if (localStorage.getItem('vibeCheckDate') === today()) {
          const myKey = localStorage.getItem('vibeCheckVote');
          setMyVote(myKey);
          const winner = Object.entries(v).sort((a, b) => b[1] - a[1])[0][0];
          setWinnerKey(winner);
        } else {
          setMyVote(null);
          setWinnerKey(null);
        }
      }
      setLoading(false);
    });

    return unsub;
  }, []);

  const vote = async key => {
    if (myVote) return;
    const t = today();

    // Optimistic update first — set state & localStorage before the write so
    // the onSnapshot that fires during the await sees the date already set and
    // doesn't clear myVote mid-flight.
    localStorage.setItem('vibeCheckDate', t);
    localStorage.setItem('vibeCheckVote', key);
    const updated = { ...votes, [key]: (votes[key] ?? 0) + 1 };
    const winner = Object.entries(updated).sort((a, b) => b[1] - a[1])[0][0];
    setMyVote(key);
    setWinnerKey(winner);
    const newStreak = updateStreak();
    setStreak(newStreak);
    playSkitter();
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);

    const periodRefs = [
      doc(db, 'vibeCheckHistory', `day-${t}`),
      doc(db, 'vibeCheckHistory', `week-${getISOWeek()}`),
      doc(db, 'vibeCheckHistory', `month-${t.slice(0, 7)}`),
      doc(db, 'vibeCheckHistory', 'alltime'),
    ];

    try {
      // Use setDoc+merge instead of updateDoc so it works even if the doc
      // somehow doesn't exist yet.
      await Promise.all([
        setDoc(doc(db, 'vibeCheck', 'current'), { [`votes.${key}`]: increment(1) }, { merge: true }),
        ...periodRefs.map(r => setDoc(r, { [`vote_${key}`]: increment(1) }, { merge: true })),
      ]);
    } catch (_) {
      // Write failed — undo the optimistic update
      localStorage.removeItem('vibeCheckDate');
      localStorage.removeItem('vibeCheckVote');
      setMyVote(null);
      setWinnerKey(null);
    }
  };

  const shareVote = useCallback(async () => {
    const opt = ALL_OPTIONS.find(o => o.key === myVote);
    const text = `Just checked Abel's vibe — today's verdict: "${opt?.label} ${opt?.emoji}" 👀 go vote yourself!`;
    const url = 'https://www.abeliciousness.com';
    if (navigator.share) {
      try { await navigator.share({ title: "Abel's Vibe Check", text, url }); } catch (_) {}
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    }
  }, [myVote]);

  useEffect(() => {
    if (!myVote) return;
    setPeriodLoading(true);
    getDoc(doc(db, 'vibeCheckHistory', PERIOD_DOC(activePeriod))).then(snap => {
      const data = snap.data() ?? {};
      const pv = {};
      ALL_OPTIONS.forEach(o => { pv[o.key] = data[`vote_${o.key}`] ?? 0; });
      setPeriodVotes(pv);
      setPeriodLoading(false);
    });
  }, [activePeriod, myVote]);

  // Midnight countdown so user knows when they can vote again
  useEffect(() => {
    if (!myVote) return;
    const tick = () => setCountdown(fmtCountdown(msToMidnight()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [myVote]);

  // Today count — live
  useEffect(() => {
    const ref = doc(db, 'vibeCheckHistory', `day-${today()}`);
    const unsub = onSnapshot(ref, snap => {
      const data = snap.data() ?? {};
      const sum = ALL_OPTIONS.reduce((acc, o) => acc + (data[`vote_${o.key}`] ?? 0), 0);
      setTodayCount(sum);
    });
    return unsub;
  }, []);

  // Caption — live
  useEffect(() => {
    const ref = doc(db, 'vibeCheck', 'caption');
    const unsub = onSnapshot(ref, snap => {
      setCaption(snap.exists() ? snap.data()?.text ?? null : null);
    });
    return unsub;
  }, []);

  // Trend — fetch once on mount
  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'vibeCheckHistory', `week-${getISOWeek()}`)),
      getDoc(doc(db, 'vibeCheckHistory', `week-${getPrevISOWeek()}`)),
    ]).then(([thisSnap, prevSnap]) => {
      const thisScore = computeScore(thisSnap.data() ?? {});
      const prevScore = computeScore(prevSnap.data() ?? {});
      if (prevScore === 0) return;
      const diff = thisScore - prevScore;
      setTrendDir(diff > 0.15 ? 'up' : diff < -0.15 ? 'down' : 'same');
    }).catch(() => {});
  }, []);

  // Souls lurking — presence heartbeat
  useEffect(() => {
    const sessionId = sessionIdRef.current;
    const presenceRef = doc(db, 'vibeCheckPresence', sessionId);
    const writePresence = () =>
      setDoc(presenceRef, { lastSeen: Date.now() }, { merge: true }).catch(() => {});
    writePresence();
    const heartbeat = setInterval(writePresence, 30000);

    const unsub = onSnapshot(collection(db, 'vibeCheckPresence'), snap => {
      const cutoff = Date.now() - 90000;
      setSoulsLurking(snap.docs.filter(d => (d.data().lastSeen ?? 0) > cutoff).length);
    });

    return () => {
      clearInterval(heartbeat);
      deleteDoc(presenceRef).catch(() => {});
      unsub();
    };
  }, []);

  // Hot takes — live
  useEffect(() => {
    const q = query(
      collection(db, 'vibeCheckTakes'),
      where('date', '==', today()),
      orderBy('ts', 'desc'),
      limit(8)
    );
    const unsub = onSnapshot(q, snap => {
      setTakes(snap.docs.map(d => d.data()));
    });
    return unsub;
  }, []);

  const submitTake = async () => {
    const text = takeInput.trim().slice(0, 60);
    if (!text || takeSubmitted || !myVote) return;
    setTakeSubmitted(true);
    try {
      await addDoc(collection(db, 'vibeCheckTakes'), {
        text,
        optionKey: myVote,
        date: today(),
        ts: Date.now(),
      });
      setTakeInput('');
    } catch (_) { setTakeSubmitted(false); }
  };

  const total = Object.values(votes).reduce((a, b) => a + b, 0);

  if (loading) return null;

  return (
    <Wrapper>
      <Title>⚡ Vibe Check ⚡</Title>

      {/* Subtitle with live stats */}
      <Subtitle>
        which version of Abel currently exists this week?
        {caption && <Caption>"{caption}"</Caption>}
        <Dim>
          {myVote
            ? <>vibe shifts at midnight · {total.toLocaleString()} souls weighed in</>
            : <>one verdict per day · {total.toLocaleString()} souls weighed in</>}
          {trendDir === 'up' && <TrendBadge up> ↑ vibing up</TrendBadge>}
          {trendDir === 'down' && <TrendBadge> ↓ vibing down</TrendBadge>}
          {trendDir === 'same' && <TrendBadge neutral> → holding steady</TrendBadge>}
        </Dim>
        {soulsPhrase(soulsLurking) && (
          <SoulsLine>{soulsPhrase(soulsLurking)}</SoulsLine>
        )}
      </Subtitle>

      {/* ── VOTING VIEW ─────────────────────────── */}
      {!myVote && (
        <>
          {TIERS.map(tier => (
            <TierBlock key={tier.label}>
              <TierLabel color={tier.color}>{tier.label}</TierLabel>
              <TierOptions>
                {tier.options.map(opt => (
                  <Option
                    key={opt.key}
                    onClick={() => vote(opt.key)}
                    $tierColor={tier.borderColor}
                  >
                    <OptionTop>
                      <Emoji>{opt.emoji}</Emoji>
                      <OptionLabel>{opt.label}</OptionLabel>
                    </OptionTop>
                  </Option>
                ))}
              </TierOptions>
            </TierBlock>
          ))}
          <Hint>Tap any option to cast your anonymous verdict 🗳️</Hint>
        </>
      )}

      {/* ── RESULTS VIEW ────────────────────────── */}
      {myVote && winnerKey && (
        <ResultsView ref={resultsRef}>

          {/* Winner banner */}
          {(() => {
            const tier = getTierForKey(winnerKey);
            const opt = tier?.options.find(o => o.key === winnerKey);
            return (
              <WinnerBanner $tierColor={tier?.color}>
                <WinnerEmoji>{opt?.emoji}</WinnerEmoji>
                <WinnerLabel>{opt?.label}</WinnerLabel>
                <WinnerTier color={tier?.color}>{tier?.label}</WinnerTier>
                <WinnerComment>{getPostVoteComment(winnerKey)}</WinnerComment>
              </WinnerBanner>
            );
          })()}

          {/* Stats row */}
          <StatsRow>
            {total > 0 && (
              <StatChip>
                {Math.round(((votes[myVote] ?? 0) / total) * 100)}% agree with you
              </StatChip>
            )}
            <StatChip>{todayCount} voted today</StatChip>
            {streak > 1 && <StatChip>🔥 {streak}-day streak</StatChip>}
          </StatsRow>

          {/* Sorted result bars */}
          <ResultBars>
            {[...ALL_OPTIONS]
              .sort((a, b) => (votes[b.key] ?? 0) - (votes[a.key] ?? 0))
              .map((opt, i) => {
                const count = votes[opt.key] ?? 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                const tier = getTierForKey(opt.key);
                const isMe = myVote === opt.key;
                return (
                  <ResultBar key={opt.key} $isMe={isMe}>
                    <ResultBarMeta>
                      <span>{opt.emoji}</span>
                      <ResultBarName>{opt.label}</ResultBarName>
                      {isMe && <YouMark>you</YouMark>}
                      <ResultBarPct>{pct}%</ResultBarPct>
                    </ResultBarMeta>
                    <ResultBarTrack>
                      <ResultBarFill
                        style={{ width: `${pct}%` }}
                        color={tier?.color}
                        $delay={i * 55}
                      />
                    </ResultBarTrack>
                  </ResultBar>
                );
              })}
          </ResultBars>

          {/* Share button */}
          <ShareBtn onClick={shareVote}>
            {copied ? '✅ Copied!' : '🔗 Share your verdict'}
          </ShareBtn>

          {/* Hot take input */}
          {!takeSubmitted ? (
            <TakeForm>
              <TakeInputField
                type="text"
                placeholder="drop a take (anonymous, 60 chars)"
                value={takeInput}
                onChange={e => setTakeInput(e.target.value.slice(0, 60))}
                onKeyDown={e => e.key === 'Enter' && submitTake()}
                maxLength={60}
              />
              <TakeSubmitBtn
                type="button"
                onClick={submitTake}
                disabled={!takeInput.trim()}
              >
                send it
              </TakeSubmitBtn>
            </TakeForm>
          ) : (
            <TakeConfirm>take logged 👁️</TakeConfirm>
          )}

          {/* Gossip ticker */}
          {takes.length > 0 && (
            <TakesSection>
              <TakesLabel>👁️ what the cursed internet is saying</TakesLabel>
              {takes.map((take, i) => (
                <Take key={i} $delay={i * 60}>
                  <TakeEmoji>
                    {ALL_OPTIONS.find(o => o.key === take.optionKey)?.emoji ?? '👻'}
                  </TakeEmoji>
                  <TakeText>"{take.text}"</TakeText>
                </Take>
              ))}
            </TakesSection>
          )}

          {/* Analytics tabs */}
          <AnalyticsSection>
            <AnalyticsHeader>📊 Community Analytics</AnalyticsHeader>
            <PeriodTabs>
              {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                <PTab key={key} $active={activePeriod === key} onClick={() => setActivePeriod(key)}>
                  {label}
                </PTab>
              ))}
            </PeriodTabs>
            {periodLoading ? (
              <LoadingText>Loading stats…</LoadingText>
            ) : (() => {
              const grandTotal = Object.values(periodVotes).reduce((a, b) => a + b, 0);
              return (
                <>
                  {TIERS.map(tier => {
                    const activeOpts = tier.options.filter(opt => (periodVotes[opt.key] ?? 0) > 0);
                    if (activeOpts.length === 0) return null;
                    return (
                      <AnalyticsTier key={tier.label}>
                        <ATierLabel color={tier.color}>{tier.label}</ATierLabel>
                        {activeOpts.map(opt => {
                          const count = periodVotes[opt.key] ?? 0;
                          const pct = grandTotal > 0 ? Math.round((count / grandTotal) * 100) : 0;
                          return (
                            <ABar key={opt.key} $isMyVote={myVote === opt.key}>
                              <ABarTop>
                                <ABarLabel>
                                  <span>{opt.emoji}</span>
                                  <span>{opt.label}</span>
                                </ABarLabel>
                                {myVote === opt.key && <MyMark>you</MyMark>}
                                <ABarStats>{pct}% <ABarCount>({count})</ABarCount></ABarStats>
                              </ABarTop>
                              <ABarTrack>
                                <ABarFill style={{ width: `${pct}%` }} color={tier.color} />
                              </ABarTrack>
                            </ABar>
                          );
                        })}
                      </AnalyticsTier>
                    );
                  })}
                  <ATotalLine>{grandTotal.toLocaleString()} vote{grandTotal !== 1 ? 's' : ''} · {PERIOD_LABELS[activePeriod].toLowerCase()}</ATotalLine>
                </>
              );
            })()}
          </AnalyticsSection>

          {/* Countdown */}
          <VoteAgainLine>
            🔒 Come back in <Countdown>{countdown}</Countdown> to vote again
          </VoteAgainLine>
        </ResultsView>
      )}
    </Wrapper>
  );
};

export default VibeCheck;

// ── styles ─────────────────────────────────────────────────────────────────────


const Wrapper = styled.div`
  width: 100%;
  max-width: 620px;
  margin: 40px auto;
  padding: 32px 24px;
  background: rgba(0, 0, 0, 0.65);
  border: 2px solid rgba(76, 0, 101, 0.5);
  border-radius: 18px;
  font-family: 'Creepster', Arial, sans-serif;
  box-sizing: border-box;
  @media (max-width: 480px) { padding: 24px 16px; }
  @media (max-width: 360px) { padding: 18px 10px; border-radius: 12px; }
`;

const Title = styled.h2`
  color: white;
  font-size: 2rem;
  text-align: center;
  margin: 0 0 8px;
  text-shadow: 0 0 14px rgba(148, 0, 211, 0.7);
  @media (max-width: 430px) { font-size: 1.5rem; }
`;

const Subtitle = styled.div`
  text-align: center;
  color: rgba(255,255,255,0.55);
  font-size: 0.9rem;
  margin: 0 0 24px;
  line-height: 1.5;
`;

const Dim = styled.span`
  font-size: 0.8rem;
  color: rgba(255,255,255,0.3);
`;

const Caption = styled.div`
  font-style: italic;
  color: rgba(255,255,255,0.45);
  font-size: 0.85rem;
  margin-top: 6px;
`;

const TrendBadge = styled.span`
  margin-left: 8px;
  font-size: 0.75rem;
  color: ${({ up, neutral }) => up ? '#44ff88' : neutral ? 'rgba(255,255,255,0.4)' : '#ff6666'};
`;

const TierBlock = styled.div`
  margin-bottom: 16px;
`;

const TierLabel = styled.div`
  font-size: 0.8rem;
  color: ${({ color }) => color};
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 6px;
  padding-left: 4px;
  border-left: 3px solid ${({ color }) => color};
  padding-left: 8px;
`;

const TierOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Option = styled.button`
  width: 100%;
  background: rgba(255,255,255,0.03);
  border: 1.5px solid ${({ $tierColor }) => $tierColor};
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s;

  &:hover {
    background: rgba(76, 0, 101, 0.2);
    border-color: rgba(148, 0, 211, 0.5);
  }
`;

const OptionTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Emoji = styled.span`
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const OptionLabel = styled.span`
  color: rgba(255,255,255,0.8);
  font-size: 1rem;
  flex: 1;
`;

const Hint = styled.p`
  text-align: center;
  margin-top: 18px;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.3);
`;

const ResultsView = styled.div`
  scroll-margin-top: 80px;
`;

const WinnerBanner = styled.div`
  text-align: center;
  padding: 24px 16px 20px;
  background: ${({ $tierColor }) => $tierColor ? `linear-gradient(135deg, rgba(0,0,0,0.6), rgba(0,0,0,0.3))` : 'rgba(76,0,101,0.3)'};
  border: 2px solid ${({ $tierColor }) => $tierColor || 'rgba(148,0,211,0.5)'};
  border-radius: 14px;
  margin-bottom: 16px;
  box-shadow: 0 0 30px ${({ $tierColor }) => $tierColor ? `${$tierColor}33` : 'rgba(148,0,211,0.2)'};
`;

const WinnerEmoji = styled.div`
  font-size: 3.5rem;
  margin-bottom: 6px;
  animation: ${() => keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.12)}`} 2s ease infinite;
`;

const WinnerLabel = styled.div`
  font-size: 1.4rem;
  color: white;
  font-weight: bold;
  margin-bottom: 4px;
  @media(max-width:430px){font-size:1.1rem}
`;

const WinnerTier = styled.div`
  font-size: 0.8rem;
  color: ${({ color }) => color || '#cc44ff'};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 10px;
`;

const WinnerComment = styled.div`
  font-size: 0.9rem;
  color: rgba(255,255,255,0.65);
  line-height: 1.4;
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
`;

const StatChip = styled.div`
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.6);
`;

const ResultBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

const ResultBar = styled.div`
  padding: 8px 10px;
  background: ${({ $isMe }) => $isMe ? 'rgba(76,0,101,0.25)' : 'rgba(255,255,255,0.03)'};
  border: 1px solid ${({ $isMe }) => $isMe ? 'rgba(148,0,211,0.35)' : 'rgba(255,255,255,0.05)'};
  border-radius: 8px;
`;

const ResultBarMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.88rem;
  color: rgba(255,255,255,0.8);
  @media (max-width: 360px) { font-size: 0.78rem; gap: 4px; }
`;

const ResultBarName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @media (max-width: 400px) { white-space: normal; line-height: 1.2; }
`;

const YouMark = styled.span`
  font-size: 0.62rem;
  color: #cc44ff;
  background: rgba(148,0,211,0.2);
  border-radius: 4px;
  padding: 1px 5px;
`;

const ResultBarPct = styled.span`
  font-size: 0.8rem;
  color: rgba(255,255,255,0.5);
  min-width: 36px;
  text-align: right;
  flex-shrink: 0;
`;

const ResultBarTrack = styled.div`
  height: 10px;
  background: rgba(255,255,255,0.07);
  border-radius: 5px;
  overflow: hidden;
`;

const barReveal = keyframes`from { width: 0% }`;

const ResultBarFill = styled.div`
  height: 100%;
  background: ${({ color }) => color || '#7b00d4'};
  border-radius: 4px;
  opacity: 0.85;
  animation: ${barReveal} 0.7s ease both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;
`;

const ShareBtn = styled.button`
  display: block;
  margin: 0 auto 20px;
  padding: 10px 24px;
  background: rgba(76,0,101,0.5);
  border: 1.5px solid rgba(148,0,211,0.5);
  border-radius: 20px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(76,0,101,0.8); border-color: rgba(148,0,211,0.9); transform: scale(1.03); }
`;

// ── analytics styles ──────────────────────────────────────────────────────────

const analyticsSlideUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const barGrow = keyframes`
  from { width: 0%; }
`;

const AnalyticsSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.08);
  animation: ${analyticsSlideUp} 0.45s ease both;
`;

const AnalyticsHeader = styled.h3`
  color: rgba(255,255,255,0.65);
  font-size: 1rem;
  text-align: center;
  margin: 0 0 14px;
  letter-spacing: 1px;
`;

const PeriodTabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const PTab = styled.button`
  flex: 1;
  min-width: 60px;
  padding: 8px 6px;
  border-radius: 8px;
  border: 1.5px solid ${({ $active }) => ($active ? 'rgba(148,0,211,0.8)' : 'rgba(255,255,255,0.1)')};
  background: ${({ $active }) => ($active ? 'rgba(76,0,101,0.55)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'white' : 'rgba(255,255,255,0.45)')};
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { border-color: rgba(148,0,211,0.5); color: white; }
  @media (max-width: 360px) { font-size: 0.72rem; padding: 6px 4px; }
`;

const AnalyticsTier = styled.div`
  margin-bottom: 16px;
`;

const ATierLabel = styled.div`
  font-size: 0.7rem;
  color: ${({ color }) => color};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  padding-left: 8px;
  border-left: 3px solid ${({ color }) => color};
`;

const ABar = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  background: ${({ $isMyVote }) => ($isMyVote ? 'rgba(76,0,101,0.2)' : 'transparent')};
  border-radius: 6px;
  border: 1px solid ${({ $isMyVote }) => ($isMyVote ? 'rgba(148,0,211,0.25)' : 'transparent')};
`;

const ABarTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
  @media (max-width: 360px) { gap: 4px; }
`;

const ABarLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.75);
  flex: 1;
  min-width: 0;
  & > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  @media (max-width: 360px) { font-size: 0.78rem; }
`;

const MyMark = styled.span`
  font-size: 0.62rem;
  color: #cc44ff;
  background: rgba(148,0,211,0.2);
  border-radius: 4px;
  padding: 1px 5px;
`;

const ABarStats = styled.div`
  font-size: 0.8rem;
  color: rgba(255,255,255,0.5);
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 48px;
  text-align: right;
`;

const ABarCount = styled.span`
  font-size: 0.7rem;
  color: rgba(255,255,255,0.28);
`;

const ABarTrack = styled.div`
  height: 10px;
  background: rgba(255,255,255,0.06);
  border-radius: 5px;
  overflow: hidden;
`;

const ABarFill = styled.div`
  height: 100%;
  background: ${({ color }) => color};
  border-radius: 5px;
  transition: width 0.65s ease;
  animation: ${barGrow} 0.65s ease both;
  opacity: 0.8;
`;

const ATotalLine = styled.p`
  text-align: center;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.22);
  margin: 4px 0 0;
  font-style: italic;
`;

const VoteAgainLine = styled.div`
  text-align: center;
  font-size: 0.82rem;
  color: rgba(255,255,255,0.35);
  margin-top: 10px;
  padding: 8px;
`;

const Countdown = styled.span`
  font-size: 0.9rem;
  color: #cc44ff;
  letter-spacing: 2px;
  font-variant-numeric: tabular-nums;
`;

const soulsPulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; text-shadow: 0 0 10px rgba(148, 0, 211, 0.8); }
`;

const SoulsLine = styled.div`
  margin-top: 6px;
  font-size: 0.78rem;
  color: rgba(148, 0, 211, 0.85);
  letter-spacing: 0.5px;
  animation: ${soulsPulse} 3.5s ease-in-out infinite;
`;

const TakeForm = styled.div`
  display: flex;
  gap: 8px;
  margin: 0 0 20px;
`;

const TakeInputField = styled.input`
  flex: 1;
  padding: 9px 12px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(148, 0, 211, 0.3);
  border-radius: 8px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.9rem;
  outline: none;
  &::placeholder { color: rgba(255,255,255,0.3); }
  &:focus { border-color: rgba(148, 0, 211, 0.8); }
`;

const TakeSubmitBtn = styled.button`
  padding: 9px 16px;
  background: rgba(76, 0, 101, 0.6);
  border: 1px solid rgba(148, 0, 211, 0.5);
  border-radius: 8px;
  color: white;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  &:hover:not(:disabled) { background: rgba(76, 0, 101, 0.9); }
  &:disabled { opacity: 0.35; cursor: default; }
`;

const TakeConfirm = styled.div`
  text-align: center;
  font-size: 0.82rem;
  color: rgba(148, 0, 211, 0.7);
  margin-bottom: 20px;
  letter-spacing: 1px;
`;

const takeSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const TakesSection = styled.div`
  margin-bottom: 24px;
  border-top: 1px solid rgba(148, 0, 211, 0.15);
  padding-top: 16px;
`;

const TakesLabel = styled.div`
  font-size: 0.72rem;
  color: rgba(148, 0, 211, 0.6);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const Take = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 7px;
  padding: 8px 12px;
  background: rgba(76, 0, 101, 0.1);
  border-left: 2px solid rgba(148, 0, 211, 0.45);
  border-radius: 0 8px 8px 0;
  animation: ${takeSlideIn} 0.35s ease both;
  animation-delay: ${({ $delay }) => $delay || 0}ms;
  transition: background 0.2s;
  &:hover { background: rgba(76, 0, 101, 0.18); }
`;

const TakeEmoji = styled.span`
  font-size: 1rem;
  flex-shrink: 0;
  line-height: 1.5;
`;

const TakeText = styled.span`
  font-size: 0.85rem;
  color: rgba(255,255,255,0.7);
  line-height: 1.45;
  font-style: italic;
`;

const LoadingText = styled.p`
  text-align: center;
  color: rgba(255,255,255,0.28);
  font-size: 0.85rem;
  margin: 20px 0;
`;
